import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
/**
 * Minimal clinical-tags marker for dense worklist rows: a tag glyph plus the
 * count, full labels in the tooltip. Deliberately NOT a button — clicks
 * bubble to the row handler, which expands the study card where the full
 * chips render. Renders nothing when the study carries no tags.
 */
export function DiagnosisTagGlyph({ tags, className, }) {
    const { t } = useTranslation();
    if (!tags || tags.length === 0)
        return null;
    const summary = tags
        .map((tag) => (tag.qualifier === 'suspected' ? `${tag.label}?` : tag.label))
        .join(', ');
    return (_jsxs("span", { className: cn('inline-flex w-fit items-center gap-1 rounded-full border border-teal-500/30 bg-teal-500/10 px-1.5 py-px text-[10px] leading-4 text-teal-300', className), title: t('tags.glyphTitle', {
            summary,
            defaultValue: 'Clinical tags: {{summary}} — expand for details',
        }), "data-testid": "diagnosis-tag-glyph", children: [_jsx(Tag, { className: "h-2.5 w-2.5", "aria-hidden": true }), _jsx("span", { className: "font-mono tabular-nums", children: tags.length })] }));
}
/**
 * One chip. Qualifier styling matches the editor field: suspected gets a
 * trailing '?', ruled_out is struck through, so a negated finding can never
 * read as an assertion at a glance.
 */
function TagChip({ tag, fullLabel }) {
    const { t } = useTranslation();
    return (_jsx("span", { className: cn('inline-block rounded-full border px-1.5 py-px text-[10px] leading-4', !fullLabel && 'max-w-36 truncate', tag.qualifier === 'ruled_out'
            ? 'border-border/40 text-muted-foreground/60 line-through'
            : tag.qualifier === 'suspected'
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                : 'border-teal-500/30 bg-teal-500/10 text-teal-300'), title: tag.qualifier === 'ruled_out'
            ? t('tags.ruledOutTitle', { label: tag.label, defaultValue: '{{label}} (ruled out)' })
            : tag.qualifier === 'suspected'
                ? t('tags.suspectedTitle', {
                    label: tag.label,
                    defaultValue: '{{label}} (suspected)',
                })
                : tag.label, children: tag.qualifier === 'suspected' ? `${tag.label}?` : tag.label }));
}
/**
 * Compact diagnosis-tag chips for worklist rows: up to `max` chips plus a
 * "+N" overflow button. The hidden labels are in its title for a mouse, and a
 * tap or click opens them as chips: titles never show on touch.
 */
export function DiagnosisTagChips({ tags, max = 3, className, fullLabels = false, }) {
    const { t } = useTranslation();
    if (!tags || tags.length === 0)
        return null;
    const visible = tags.slice(0, max);
    const overflow = tags.slice(max);
    // The chips sit inside clickable worklist rows (a double-click on a
    // teleconsultation row opens the viewer). Events inside the popover bubble
    // through its portal to the row too, so both ends stop them.
    const stop = (e) => e.stopPropagation();
    return (_jsxs("span", { className: cn('flex flex-wrap items-center gap-1', className), "data-testid": "diagnosis-tag-chips", children: [visible.map((tag) => (_jsx(TagChip, { tag: tag, fullLabel: fullLabels }, `${tag.label}|${tag.qualifier}`))), overflow.length > 0 && (_jsxs(Popover, { children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { type: "button", className: "text-muted-foreground/70 border-border/40 hover:text-foreground touch-hitbox relative inline-block rounded-full border px-1.5 py-px text-[10px] leading-4", title: overflow.map((tag) => tag.label).join(', '), "aria-label": t('tags.showMore', { count: overflow.length }), "data-testid": "diagnosis-tag-overflow", onClick: stop, onDoubleClick: stop, onKeyDown: stop, children: ["+", overflow.length] }) }), _jsx(PopoverContent, { align: "start", className: "flex w-auto max-w-72 flex-wrap gap-1 p-2", onClick: stop, onDoubleClick: stop, onKeyDown: stop, "data-testid": "diagnosis-tag-overflow-list", children: overflow.map((tag) => (_jsx(TagChip, { tag: tag, fullLabel: true }, `${tag.label}|${tag.qualifier}`))) })] }))] }));
}
